import React, { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import AdminLayout from "../../components/layouts/AdminLayout";
import { firestore } from "../../firebaseConfig";

const AdminCompanyMessages = () => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const selectedThreadMeta = threads.find((thread) => thread.id === selectedThread) || null;

  useEffect(() => {
    if (!firestore) return undefined;
    const q = query(collection(firestore, "companyMessages"), orderBy("lastMessageAt", "desc"));
    return onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setThreads(rows);
    });
  }, []);

  useEffect(() => {
    if (!firestore || !selectedThread) {
      setThreadMessages([]);
      return undefined;
    }
    const q = query(
      collection(firestore, "companyMessages", selectedThread, "messages"),
      orderBy("createdAt", "asc")
    );
    return onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setThreadMessages(rows);
    });
  }, [selectedThread]);

  useEffect(() => {
    if (!firestore) return undefined;
    const q = query(collection(firestore, "messageModerationIncidents"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setIncidents(rows);
    });
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Company Messages</h1>
          <p className="text-sm text-slate-600">
            Review who each company is messaging (clients/farmers) and inspect moderation incidents.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-1 bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
              Threads
            </div>
            <div className="max-h-[28rem] overflow-y-auto divide-y divide-slate-100">
              {threads.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No company conversations yet.</p>}
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => setSelectedThread(thread.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${
                    selectedThread === thread.id ? "bg-slate-50" : ""
                  }`}
                >
                  <p className="text-sm font-medium text-slate-800">{thread.companyName || "Company"}</p>
                  <p className="text-xs text-slate-500">To: {thread.participantId || "Unknown participant"}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
              {selectedThreadMeta
                ? `Messages: ${selectedThreadMeta.companyName || "Company"} → ${
                    selectedThreadMeta.participantId || "Participant"
                  }`
                : "Select a thread"}
            </div>
            <div className="max-h-[28rem] overflow-y-auto px-4 py-3 space-y-2 bg-slate-50">
              {selectedThread && threadMessages.length === 0 && (
                <p className="text-sm text-slate-500">No messages in this thread.</p>
              )}
              {!selectedThread && <p className="text-sm text-slate-500">Choose a thread to inspect messages.</p>}
              {threadMessages.map((message) => (
                <div key={message.id} className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">
                    {message.sender === "company"
                      ? `Company${message.companyResponderInitials ? ` (${message.companyResponderInitials})` : ""}`
                      : "Participant"}
                  </p>
                  <p className="text-sm text-slate-800 whitespace-pre-line">{message.text}</p>
                  {message?.file?.fileName && (
                    <p className="text-xs text-emerald-700 mt-1">📎 {message.file.fileName}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
            Moderation incidents
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {incidents.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-sm text-slate-500 text-center">
                      No moderation incidents reported.
                    </td>
                  </tr>
                )}
                {incidents.map((incident) => (
                  <tr key={incident.id}>
                    <td className="px-4 py-3 text-sm text-slate-800">{incident.companyName || incident.companyKey || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{incident.participantId || "—"}</td>
                    <td className="px-4 py-3 text-sm text-red-700">{incident.reason || "Policy violation"}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{incident.messageText || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCompanyMessages;
